// src/components/InquiryModal.jsx
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  doc,
  setDoc,
  runTransaction,
  serverTimestamp,
  collection,
} from "firebase/firestore";

const InquiryModal = ({ isOpen, onClose, product }) => {
  const [step, setStep] = useState(1); // 1=ask location, 2=form, 3=success
  const [loc, setLoc] = useState(null); // {lat, lng}
  const [loadingLoc, setLoadingLoc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const year = new Date().getFullYear(); 
  const [form, setForm] = useState({ name: "", phone: "" });

  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setLoc(null);
      setErr("");
      setForm({ name: "", phone: "" });
      setSubmitting(false);
      setLoadingLoc(false);
    }
  }, [isOpen]);

  if (!isOpen || !product) return null;

  const requestLocation = () => {
    setErr("");
    setLoadingLoc(true);
    if (!navigator.geolocation) {
      setErr("Geolocation is not supported on this device.");
      setLoadingLoc(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: Number(pos.coords.latitude),
          lng: Number(pos.coords.longitude),
        };
        setLoc(coords);
        setLoadingLoc(false);
        setStep(2);
      },
      () => {
        setErr("Location is required to proceed.");
        setLoadingLoc(false);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  // helper: atomically increment counter stored at metadata/inquiries and return the new number
  async function getNextInquiryNumber() {
  const metaRef = doc(db, "metadata", "inquiries");

  const next = await runTransaction(db, async (tx) => {
    const snap = await tx.get(metaRef);

    let nextNumber = 1;

    if (snap.exists()) {
      const data = snap.data();
      const lastYear = data.year;
      const last = typeof data.last === "number" ? data.last : 0;

      if (lastYear === year) {
        nextNumber = last + 1;
      }
    }

    tx.set(
      metaRef,
      {
        year: year,
        last: nextNumber,
      },
      { merge: true }
    );

    return nextNumber;
  });

  return next;
}


  const handleSubmit = async () => {
    setErr("");

    const name = form.name.trim();
    const phone = form.phone.trim();

    if (!name || !phone) {
      setErr("Please enter your name and phone number.");
      return;
    }

    // ✅ Phone number must be exactly 10 digits
    if (!/^\d{10}$/.test(phone)) {
      setErr("Please enter a valid 10-digit phone number.");
      return;
    }

    if (!loc?.lat || !loc?.lng) {
      setErr("Location missing. Please allow location to continue.");
      setStep(1);
      return;
    }

    const docData = {
      name,
      phone,
      lat: loc.lat,
      lng: loc.lng,
      product: {
        id: product.id ?? null,
        title: product.title ?? product.name ?? "",
        brand: product.brand ?? null,
        category: product.category ?? null,
        image: product.img ?? null,
        mrp: product.mrp ?? null,
        price: product.price ?? null,
      },
      createdAt: serverTimestamp(),
      isClosed: false,
    };

    try {
      setSubmitting(true);

      // get next counter and build ID like "INQ-00001"
      const nextNumber = await getNextInquiryNumber();
      const idString = `INQ-${year}-${String(nextNumber).padStart(5, "0")}`;

      // write with deterministic ID
      const inquiryRef = doc(db, "inquiries", idString);
      await setDoc(inquiryRef, docData);

      setSubmitting(false);
      setStep(3);
    } catch (e) {
      setSubmitting(false);
      setErr("Could not submit inquiry. Please try again.");
      console.error("Inquiry save error:", e);
    }
  };

  return (
    <div className="inquiry-overlay" role="dialog" aria-modal="true">
      <div className="inquiry-modal">
        <button className="inquiry-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        {step === 1 && (
          <div className="inquiry-step">
            <h3 className="inquiry-title">Allow Location</h3>
            <p className="inquiry-sub">
              We need your location to connect you with the nearest store.
            </p>

            <div className="inquiry-product">
              <img src={product.img} alt={product.title} />
              <div>
                <p className="prod-title">{product.title}</p>
                {product.price != null && (
                  <p className="prod-price">₹{Number(product.price).toLocaleString()}</p>
                )}
              </div>
            </div>

            {err && <p className="inquiry-error">{err}</p>}
            <button
              className="inquiry-primary"
              onClick={requestLocation}
              disabled={loadingLoc}
            >
              {loadingLoc ? "Detecting…" : "Share Location"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="inquiry-step">
            <h3 className="inquiry-title">Inquiry Details</h3>

            <div className="inquiry-product">
              <img src={product.img} alt={product.title} />
              <div>
                <p className="prod-title">{product.title}</p>
                {product.price != null && (
                  <p className="prod-price">₹{Number(product.price).toLocaleString()}</p>
                )}
              </div>
            </div>

            <div className="inquiry-field">
              <label>Your Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Enter your full name"
              />
            </div>

            <div className="inquiry-field">
              <label>Phone Number</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => {
                  // Allow any digits, don't trim, so autofill/paste works fully
                  const val = e.target.value.replace(/\D/g, "");
                  setForm((f) => ({ ...f, phone: val }));
                }}
                placeholder="10-digit mobile number"
                inputMode="numeric"
                required
                style={{
                  borderColor: err && !/^\d{10}$/.test(form.phone) ? "red" : undefined,
                }}
              />
            </div>

            {err && <p className="inquiry-error" style={{ color: "red" }}>{err}</p>}

            <button
              className="inquiry-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit Inquiry"}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="inquiry-step center">
            <h3 className="inquiry-title success">Inquiry Registered ✅</h3>
            <p className="inquiry-sub">
              Our nearest store will contact you to confirm details, complete the billing, and arrange delivery.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InquiryModal;
